/* =========================================================================
   SAMSARA — SECTION-HOME-HERO.JS
   Sprint 2 / T1 cinematic pivot 2026-04-24.

   Modules:
     1. Scroll indicator — click → smooth scroll la #prajire / #cafea-saptamana /
        viewport (soft-fail). Hide class toggle cu rAF throttle peste 100px.
     2. Countdown — citește data-roast-day / data-roast-time / data-order-cutoff
        de pe hero root (setate din settings Sprint 1 T10).
        - State A (default): d:h:m:s până la următoarea prăjire
        - State C (roasting now): sâmbăta între roast_time și 23:59 → zero digits,
          footnote italic "Prăjim chiar acum. Livrăm marți."
        Tick 1s. Timezone Europe/Bucharest (Intl.DateTimeFormat DST-safe).

   Respects prefers-reduced-motion — scroll uses behavior 'auto'.
   ========================================================================= */

(function () {
  'use strict';

  const SCROLL_HIDE_THRESHOLD = 100;

  const DAY_INDEX = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
  };

  const WEEKDAY_MAP = {
    sun: 'sunday',
    mon: 'monday',
    tue: 'tuesday',
    wed: 'wednesday',
    thu: 'thursday',
    fri: 'friday',
    sat: 'saturday',
  };

  /* ====================================================================== */
  /*   Helpers                                                              */
  /* ====================================================================== */

  function parseHHMM(str, fallbackH, fallbackM) {
    if (typeof str !== 'string') return { h: fallbackH, m: fallbackM };
    const match = /^(\d{1,2}):(\d{2})$/.exec(str.trim());
    if (!match) return { h: fallbackH, m: fallbackM };
    const h = Math.min(23, Math.max(0, parseInt(match[1], 10)));
    const m = Math.min(59, Math.max(0, parseInt(match[2], 10)));
    return { h: h, m: m };
  }

  const BUCHAREST_FMT = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Bucharest',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    weekday: 'short',
    hour12: false,
  });

  function nowBucharest() {
    const parts = BUCHAREST_FMT.formatToParts(new Date());

    const get = function (type) {
      const part = parts.find(function (p) { return p.type === type; });
      return part ? part.value : '';
    };

    let hour = parseInt(get('hour'), 10);
    // Some browsers emit '24' la miezul nopții cu hour12:false — normalize.
    if (hour === 24) hour = 0;

    const weekdayShort = get('weekday').toLowerCase();
    const weekdayFull = WEEKDAY_MAP[weekdayShort] || 'monday';

    return {
      weekday: weekdayFull,
      year: parseInt(get('year'), 10),
      month: parseInt(get('month'), 10),
      day: parseInt(get('day'), 10),
      hour: hour,
      minute: parseInt(get('minute'), 10),
      second: parseInt(get('second'), 10),
    };
  }

  /* Pauză/concediu (data-roast-override, YYYY-MM-DD, din settings.next_roast_override):
     țintă = data revenirii la roast_time, indiferent de ziua săptămânii.
     Returnează null dacă data a trecut → cădem automat pe calculul săptămânal. */
  function secondsUntilOverride(now, override, roastH, roastM) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(override || '');
    if (!m) return null;
    const target = Date.UTC(+m[1], +m[2] - 1, +m[3]);
    const today = Date.UTC(now.year, now.month - 1, now.day);
    const daysAhead = Math.round((target - today) / 86400000);
    if (daysAhead < 0) return null;
    const currentSeconds = now.hour * 3600 + now.minute * 60 + now.second;
    const targetSeconds = roastH * 3600 + roastM * 60;
    const secs = daysAhead * 86400 + (targetSeconds - currentSeconds);
    return secs > 0 ? secs : null;
  }

  /* Prăjire mutată o săptămână (data-moved-roast, YYYY-MM-DD, din settings.moved_roast_date).
     Aceleași faze ca snippets/moved-roast.liquid: după cutoff-ul obișnuit dinainte și până la
     cutoff-ul mutat (ziua dinainte, ora de cutoff) → țintă = prăjirea mutată; după el și până
     duminică → prăjirea obișnuită din săptămâna aceea nu are loc, țintă = cea din săptămâna
     următoare. Altfel null → ciclul săptămânal. Secundele sunt „locale" (ora București ca UTC). */
  function secondsUntilMovedRoast(now, moved, roastDay, roastH, roastM, cutoffH, cutoffM) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(moved || '');
    if (!m) return null;
    const DAY = 86400;
    const movedDay = Date.UTC(+m[1], +m[2] - 1, +m[3]) / 86400000;
    const today = Date.UTC(now.year, now.month - 1, now.day) / 86400000;
    const nowSec = today * DAY + now.hour * 3600 + now.minute * 60 + now.second;
    const weekStart = movedDay - ((new Date(movedDay * 86400000).getUTCDay() + 6) % 7);
    const skipped = weekStart + ((DAY_INDEX[roastDay] + 6) % 7);
    const cutoffTod = cutoffH * 3600 + cutoffM * 60 + 59;
    const prevCutoff = (skipped - 8) * DAY + cutoffTod;
    const movedCutoff = (movedDay - 1) * DAY + cutoffTod;
    const weekEnd = (weekStart + 7) * DAY - 1;
    const roastTod = roastH * 3600 + roastM * 60;
    if (nowSec > prevCutoff && nowSec <= movedCutoff) return movedDay * DAY + roastTod - nowSec;
    if (nowSec > movedCutoff && nowSec <= weekEnd) return (skipped + 7) * DAY + roastTod - nowSec;
    return null;
  }

  function secondsUntilNextRoast(now, roastDay, roastH, roastM) {
    const currentIdx = DAY_INDEX[now.weekday];
    const targetIdx = DAY_INDEX[roastDay];

    let daysAhead = ((targetIdx - currentIdx) + 7) % 7;
    const currentSeconds = now.hour * 3600 + now.minute * 60 + now.second;
    const targetSeconds = roastH * 3600 + roastM * 60;

    if (daysAhead === 0 && currentSeconds >= targetSeconds) {
      // Today's roast started — target this day next week.
      daysAhead = 7;
    }

    return Math.max(0, daysAhead * 86400 + (targetSeconds - currentSeconds));
  }

  function isRoastingNow(now, roastDay, roastH, roastM) {
    if (now.weekday !== roastDay) return false;
    const currentSeconds = now.hour * 3600 + now.minute * 60 + now.second;
    const startSeconds = roastH * 3600 + roastM * 60;
    return currentSeconds >= startSeconds;
  }

  function isPostCutoffPreRoast(now, roastDay, roastH, roastM, cutoffH, cutoffM) {
    if (now.weekday !== roastDay) return false;
    const currentSeconds = now.hour * 3600 + now.minute * 60 + now.second;
    const cutoffSeconds = cutoffH * 3600 + cutoffM * 60;
    const roastSeconds = roastH * 3600 + roastM * 60;
    return currentSeconds >= cutoffSeconds && currentSeconds < roastSeconds;
  }

  function getState(now, roastDay, roastH, roastM, cutoffH, cutoffM) {
    if (isRoastingNow(now, roastDay, roastH, roastM)) return 'C';
    if (isPostCutoffPreRoast(now, roastDay, roastH, roastM, cutoffH, cutoffM)) return 'B';
    return 'A';
  }

  function secondsUntilTodayAt(now, targetH, targetM) {
    const currentSeconds = now.hour * 3600 + now.minute * 60 + now.second;
    const targetSeconds = targetH * 3600 + targetM * 60;
    return Math.max(0, targetSeconds - currentSeconds);
  }

  function splitUnits(totalSeconds) {
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return { days: days, hours: hours, minutes: minutes, seconds: seconds };
  }

  function pad2(n) {
    return n < 10 ? '0' + n : String(n);
  }

  /* ====================================================================== */
  /*   Scroll indicator                                                     */
  /* ====================================================================== */

  function bootScrollIndicator(hero) {
    const scrollBtn = hero.querySelector('[data-home-hero-scroll]');
    if (!scrollBtn) return;

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    scrollBtn.addEventListener('click', function () {
      const target =
        document.querySelector('#prajire') ||
        document.querySelector('#cafea-saptamana') ||
        document.querySelector('#ciclu');
      const targetY = target ? target.offsetTop : window.innerHeight;

      try {
        window.scrollTo({
          top: targetY,
          behavior: prefersReducedMotion ? 'auto' : 'smooth',
        });
      } catch (e) {
        window.scrollTo(0, targetY);
      }
    });

    let ticking = false;
    let isHidden = false;

    function update() {
      const shouldHide = window.scrollY > SCROLL_HIDE_THRESHOLD;
      if (shouldHide !== isHidden) {
        scrollBtn.classList.toggle('home-hero__scroll--hidden', shouldHide);
        isHidden = shouldHide;
      }
      ticking = false;
    }

    function onScroll() {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    }

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('beforeunload', function () {
      window.removeEventListener('scroll', onScroll);
    });
  }

  /* ====================================================================== */
  /*   Countdown                                                            */
  /* ====================================================================== */

  function bootCountdown(hero) {
    const countdown = hero.querySelector('[data-home-hero-countdown]');
    if (!countdown) return;

    const roastDayRaw = (hero.getAttribute('data-roast-day') || 'saturday').toLowerCase();
    const roastDay = DAY_INDEX.hasOwnProperty(roastDayRaw) ? roastDayRaw : 'saturday';
    const roastTime = parseHHMM(hero.getAttribute('data-roast-time'), 10, 0);
    const cutoffTime = parseHHMM(hero.getAttribute('data-order-cutoff'), 8, 0);
    const roastOverride = (hero.getAttribute('data-roast-override') || '').trim();
    const movedRoast = (hero.getAttribute('data-moved-roast') || '').trim();

    const elTimer = countdown.querySelector('[data-countdown-timer]');
    const elEyebrow = countdown.querySelector('[data-countdown-eyebrow]');
    const elDays = countdown.querySelector('[data-countdown-days]');
    const elHours = countdown.querySelector('[data-countdown-hours]');
    const elMinutes = countdown.querySelector('[data-countdown-minutes]');
    const elSeconds = countdown.querySelector('[data-countdown-seconds]');
    const elFootnote = countdown.querySelector('[data-countdown-footnote]');

    const eyebrowDefault = (elEyebrow && elEyebrow.dataset.eyebrowDefault) || '';
    const eyebrowRoasting = (elEyebrow && elEyebrow.dataset.eyebrowRoasting) || '';
    const footnoteCutoff = (elFootnote && elFootnote.dataset.footnoteCutoff) || '';
    const footnoteRoasting = (elFootnote && elFootnote.dataset.footnoteRoasting) || '';

    let currentState = null; // 'A' | 'B' | 'C'
    let lastAriaMinute = -1; // cache key: minutes-since-epoch echivalent (zile*1440+min)

    function buildAriaLabel(state, units) {
      if (state === 'C') return eyebrowRoasting;
      // State A or B — describe time-to-roast la nivel de minut
      // Format: "Următoarea prăjire în {N} zile și {N} ore" (sau doar ore dacă days=0)
      if (units.days > 0) {
        return eyebrowDefault + ' în ' + units.days + ' zile și ' + units.hours + ' ore';
      }
      if (units.hours > 0) {
        return eyebrowDefault + ' în ' + units.hours + ' ore și ' + units.minutes + ' minute';
      }
      return eyebrowDefault + ' în ' + units.minutes + ' minute';
    }

    function applyState(state, units, now) {
      if (state === currentState) return;

      // Eyebrow swap
      if (elEyebrow) {
        elEyebrow.textContent = state === 'C' ? eyebrowRoasting : eyebrowDefault;
      }

      // Modifier classes
      countdown.classList.toggle('is-roasting', state === 'C');
      countdown.classList.toggle('is-cutoff', state === 'B');

      // Footnote
      if (elFootnote) {
        if (state === 'C') {
          elFootnote.textContent = footnoteRoasting;
          elFootnote.removeAttribute('hidden');
        } else if (state === 'B') {
          elFootnote.textContent = footnoteCutoff;
          elFootnote.removeAttribute('hidden');
        } else {
          elFootnote.textContent = '';
          elFootnote.setAttribute('hidden', '');
        }
      }

      currentState = state;
    }

    function render() {
      const now = nowBucharest();

      // Pauză/concediu (data-roast-override din settings.next_roast_override):
      // override activ → State A forțat, țintă = data revenirii la roast_time.
      // Stările B (cutoff) și C (roasting) n-au sens cât nu se prăjește.
      // Data trecută → overrideSecs = null → revenim automat la ciclul săptămânal.
      const overrideSecs = secondsUntilOverride(now, roastOverride, roastTime.h, roastTime.m);
      // Prăjirea mutată contează doar fără pauză activă (pauza are prioritate, ca în Liquid).
      const movedSecs = overrideSecs === null
        ? secondsUntilMovedRoast(now, movedRoast, roastDay, roastTime.h, roastTime.m, cutoffTime.h, cutoffTime.m)
        : null;

      let state = getState(now, roastDay, roastTime.h, roastTime.m, cutoffTime.h, cutoffTime.m);

      // Decide totalSeconds based on state — countdown spre target relevant
      let totalSeconds = 0;
      if (overrideSecs !== null) {
        state = 'A';
        totalSeconds = overrideSecs;
      } else if (movedSecs !== null) {
        // Săptămâna mutată: fără „Prăjim acum" în sâmbăta sărită — numărăm spre prăjirea reală.
        state = 'A';
        totalSeconds = Math.max(0, movedSecs);
      } else if (state === 'A') {
        totalSeconds = secondsUntilNextRoast(now, roastDay, roastTime.h, roastTime.m);
      } else if (state === 'B') {
        totalSeconds = secondsUntilTodayAt(now, roastTime.h, roastTime.m);
      }
      // State C: digits stay at 00:00:00:00

      const units = state === 'C'
        ? { days: 0, hours: 0, minutes: 0, seconds: 0 }
        : splitUnits(totalSeconds);

      applyState(state, units, now);

      // Update digits
      if (elDays) elDays.textContent = pad2(units.days);
      if (elHours) elHours.textContent = pad2(units.hours);
      if (elMinutes) elMinutes.textContent = pad2(units.minutes);
      if (elSeconds) elSeconds.textContent = pad2(units.seconds);

      // aria-label minute-cadenced: actualizează doar când minute-ul s-a schimbat
      // (sau forced pe state transition — aria label depinde de state).
      const minuteKey = now.weekday + '|' + now.hour * 60 + now.minute;
      // Numeric cache: combine în single int — weekday e enum, transformăm via DAY_INDEX
      const minutesNumeric = (DAY_INDEX[now.weekday] || 0) * 1440 + now.hour * 60 + now.minute;
      if (elTimer && minutesNumeric !== lastAriaMinute) {
        elTimer.setAttribute('aria-label', buildAriaLabel(state, units));
        lastAriaMinute = minutesNumeric;
      }
    }

    // First render synchronous — countdown is server-rendered visible (no
    // `hidden` attribute) ca să evităm CLS pe boot. Tabular-nums + min-width
    // pe .home-hero__countdown-unit garantează că placeholderul "--" și
    // digit-urile au lățimi identice, deci tranziția nu împinge layout-ul.
    render();

    const intervalId = window.setInterval(render, 1000);

    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) render();
    });

    window.addEventListener('beforeunload', function () {
      window.clearInterval(intervalId);
    });
  }

  /* ====================================================================== */
  /*   Boot                                                                 */
  /* ====================================================================== */

  function boot() {
    const hero = document.querySelector('[data-home-hero]');
    if (!hero) return;

    bootScrollIndicator(hero);
    bootCountdown(hero);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
